// Workflow Changes — the agency-wide workflow change-log. Every captured edit across all
// sub-accounts (who changed which workflow, when, and exactly what), with drill-down into a
// workflow's full revision history and a before/after step diff for any revision. Fed by the
// app-side watcher (ss-workflow-watch.js) → /api/workflows/*.
import { useEffect, useMemo, useState } from 'react';
import type { WorkflowRevision, WorkflowSummary, WorkflowStep } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { Button, Card, Chip, EmptyNote, ErrorNote, Select, Spinner, StatTile } from './ui';
import { formatApiTime, timeAgo } from '../../lib/time';

const DASH = '—';
const ALL = '__all__';

// Time formatting lives in lib/time.ts — see the note there about zone-less D1 timestamps. These
// were private copies (one of five across the app, several of which had the local-time bug).
const fmtRelative = (iso: string) => timeAgo(iso, { never: DASH });
const fmtWhen = (iso: string) => formatApiTime(iso, DASH);

// Human label for a sub-account: the client name when the agency has named it, else the id.
const locLabel = (r: { clientName: string | null; locationId: string | null }) =>
  r.clientName ?? r.locationId ?? 'Unknown sub-account';

const STATUS_COLOR: Record<string, string> = { published: '#16a34a', draft: '#d97706' };

// ── step diff (client-side, from the detail endpoint's before/after graphs) ──
type DiffKind = 'added' | 'removed' | 'modified' | 'same';
interface DiffRow { kind: DiffKind; step: WorkflowStep; detail: string[] }

const trunc = (v: string, n = 70) => (v.length > n ? `${v.slice(0, n)}…` : v);

function attrChanges(before: WorkflowStep, after: WorkflowStep): string[] {
  const out: string[] = [];
  if ((before.name ?? '') !== (after.name ?? '')) out.push(`name: “${before.name ?? ''}” → “${after.name ?? ''}”`);
  const keys = new Set([...Object.keys(before.attributes ?? {}), ...Object.keys(after.attributes ?? {})]);
  for (const k of keys) {
    const a = JSON.stringify((before.attributes ?? {})[k] ?? null);
    const b = JSON.stringify((after.attributes ?? {})[k] ?? null);
    if (a !== b) out.push(`${k}: ${trunc(a)} → ${trunc(b)}`);
  }
  return out;
}

// Merge before/after into one ordered list: after's steps classified added/modified/same,
// removed steps interleaved at (roughly) their old position.
function diffSteps(before: WorkflowStep[], after: WorkflowStep[]): DiffRow[] {
  const bm = new Map(before.map((s) => [s.id, s]));
  const am = new Map(after.map((s) => [s.id, s]));
  const rows: { sort: number; row: DiffRow }[] = [];
  for (const s of after) {
    const old = bm.get(s.id);
    if (!old) rows.push({ sort: s.order ?? 0, row: { kind: 'added', step: s, detail: [] } });
    else {
      const detail = attrChanges(old, s);
      rows.push({ sort: s.order ?? 0, row: { kind: detail.length ? 'modified' : 'same', step: s, detail } });
    }
  }
  for (const s of before) if (!am.has(s.id)) rows.push({ sort: (s.order ?? 0) + 0.5, row: { kind: 'removed', step: s, detail: [] } });
  return rows.sort((a, b) => a.sort - b.sort).map((r) => r.row);
}

const DIFF_STYLE: Record<DiffKind, { row: string; badge: string | null; badgeColor: string }> = {
  added: { row: 'bg-emerald-50', badge: 'added', badgeColor: '#16a34a' },
  removed: { row: 'bg-rose-50 opacity-70', badge: 'removed', badgeColor: '#e11d48' },
  modified: { row: 'bg-amber-50', badge: 'modified', badgeColor: '#d97706' },
  same: { row: '', badge: null, badgeColor: '' },
};

// The full step-by-step before/after for one revision. Loaded on demand when a
// revision is expanded in the timeline.
function RevisionDiff({ revisionId }: { revisionId: string }) {
  const [rows, setRows] = useState<DiffRow[] | null>(null);
  const [baseline, setBaseline] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    agencyApi
      .workflowRevision(revisionId)
      .then((d) => {
        setBaseline(!d.prev);
        setRows(diffSteps(d.prev?.steps ?? [], d.revision.steps));
      })
      .catch((e: Error) => setError(e.message));
  }, [revisionId]);

  if (error) return <p className="py-2 text-xs text-rose-600">Couldn’t load the diff: {error}</p>;
  if (!rows) return <p className="py-2 text-xs text-slate-400">Loading diff…</p>;
  if (rows.length === 0) return <p className="py-2 text-xs text-slate-400">No steps in this revision.</p>;

  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
      {baseline && (
        <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-500">
          First capture of this workflow — all steps shown as they were recorded.
        </div>
      )}
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => {
          const st = DIFF_STYLE[baseline ? 'same' : r.kind];
          return (
            <li key={`${r.step.id}-${i}`} className={`px-3 py-2 ${st.row}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-6 shrink-0 text-right font-mono text-xs text-slate-400">{r.step.order ?? '·'}</span>
                <span className={`text-sm text-slate-700 ${r.kind === 'removed' ? 'line-through' : ''}`}>
                  {r.step.name || r.step.type}
                </span>
                <Chip label={r.step.type} muted />
                {st.badge && <Chip label={st.badge} color={st.badgeColor} />}
              </div>
              {r.detail.length > 0 && (
                <ul className="ml-8 mt-1 space-y-0.5">
                  {r.detail.map((d, j) => (
                    <li key={j} className="break-all font-mono text-[11px] text-amber-700">{d}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── revision timeline for one workflow ──────────────────────────────
function WorkflowHistory({ wf, onBack }: { wf: WorkflowSummary; onBack: () => void }) {
  const [revisions, setRevisions] = useState<WorkflowRevision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!wf.locationId) return;
    agencyApi
      .workflowRevisions(wf.locationId, wf.workflowId)
      .then(setRevisions)
      .catch((e: Error) => setError(e.message));
  }, [wf.locationId, wf.workflowId]);

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={onBack}>← All workflow changes</Button>

      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">{wf.name ?? 'Untitled workflow'}</h2>
          {wf.status && <Chip label={wf.status} color={STATUS_COLOR[wf.status] ?? null} />}
          <Chip label={locLabel(wf)} muted />
        </div>
        <div className="mt-1 text-xs text-slate-400">
          {wf.stepCount} step{wf.stepCount === 1 ? '' : 's'} · {wf.revisionCount} revision{wf.revisionCount === 1 ? '' : 's'} ·{' '}
          {wf.changeCount} change{wf.changeCount === 1 ? '' : 's'} captured
        </div>
      </Card>

      {error && <ErrorNote error={error} />}
      {!revisions && !error && <Spinner label="Loading history…" />}

      {revisions && (
        <ol className="relative space-y-3 border-l border-slate-200 pl-5">
          {revisions.map((r) => {
            const isOpen = !!open[r.id];
            return (
              <li key={r.id} className="relative">
                <span
                  className={`absolute -left-[26px] top-4 h-2.5 w-2.5 rounded-full ${r.baseline ? 'bg-slate-300' : 'bg-brand'}`}
                />
                <Card className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">
                      {r.baseline ? 'First capture' : 'Changed'}
                    </span>
                    {r.status && <Chip label={r.status} color={STATUS_COLOR[r.status] ?? null} />}
                    <span className="text-xs text-slate-500">
                      by <span className="font-medium text-slate-600">{r.updatedByName ?? 'Unknown'}</span>
                    </span>
                    <span className="ml-auto shrink-0 text-xs text-slate-400" title={fmtWhen(r.createdAt)}>
                      {fmtRelative(r.createdAt)}
                    </span>
                  </div>
                  {(r.changes?.length ?? 0) > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {r.changes!.map((ch, i) => (
                        <li key={i} className="text-sm text-slate-600">
                          <span className="mr-1.5 text-slate-300">•</span>
                          {ch}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    onClick={() => setOpen((o) => ({ ...o, [r.id]: !isOpen }))}
                    className="mt-2 text-xs font-medium text-brand hover:underline"
                  >
                    {isOpen ? 'Hide steps' : r.baseline ? 'View steps' : 'View before / after'}
                  </button>
                  {isOpen && <RevisionDiff revisionId={r.id} />}
                </Card>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ── the page ────────────────────────────────────────────────────────
export default function WorkflowChanges() {
  const [workflows, setWorkflows] = useState<WorkflowSummary[] | null>(null);
  const [changelog, setChangelog] = useState<WorkflowRevision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState(ALL);
  const [tab, setTab] = useState<'changes' | 'workflows'>('changes');
  const [selected, setSelected] = useState<WorkflowSummary | null>(null);

  useEffect(() => {
    Promise.all([agencyApi.workflows(), agencyApi.workflowChangelog({ limit: 100 })])
      .then(([wfs, cl]) => {
        setWorkflows(wfs);
        setChangelog(cl);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  // Sub-account filter options, derived from the tracked workflows (human names first).
  const locations = useMemo(() => {
    const seen = new Map<string, string>();
    for (const w of workflows ?? []) if (w.locationId && !seen.has(w.locationId)) seen.set(w.locationId, locLabel(w));
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [workflows]);

  const visibleChanges = useMemo(
    () => (changelog ?? []).filter((r) => location === ALL || r.locationId === location),
    [changelog, location],
  );
  const visibleWorkflows = useMemo(
    () => (workflows ?? []).filter((w) => location === ALL || w.locationId === location),
    [workflows, location],
  );

  if (error) return <ErrorNote error={error} />;
  if (!workflows || !changelog) return <Spinner label="Loading workflow changes…" />;

  if (selected) return <WorkflowHistory wf={selected} onBack={() => setSelected(null)} />;

  // A changelog row → its summary row (for drill-down into history).
  const summaryFor = (r: WorkflowRevision) =>
    workflows.find((w) => w.workflowId === r.workflowId && w.locationId === r.locationId) ?? null;

  const changedWorkflows = new Set(changelog.map((r) => `${r.locationId}/${r.workflowId}`)).size;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Workflows tracked" value={workflows.length} />
        <StatTile label="Sub-accounts" value={locations.length} />
        <StatTile label="Changes captured" value={changelog.length} hint={`across ${changedWorkflows} workflow${changedWorkflows === 1 ? '' : 's'}`} />
        <StatTile label="Latest change" value={changelog[0] ? fmtRelative(changelog[0].createdAt) : DASH} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg bg-slate-100 p-0.5">
          {(['changes', 'workflows'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition ${
                tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t === 'changes' ? 'Recent changes' : 'All workflows'}
            </button>
          ))}
        </div>
        <Select value={location} onChange={setLocation} className="ml-auto">
          <option value={ALL}>All sub-accounts</option>
          {locations.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </Select>
      </div>

      {tab === 'changes' &&
        (visibleChanges.length === 0 ? (
          <EmptyNote>
            No workflow changes captured yet. Changes appear here automatically when someone edits a
            workflow in a sub-account with the widget installed.
          </EmptyNote>
        ) : (
          <div className="space-y-3">
            {visibleChanges.map((r) => {
              const wf = summaryFor(r);
              return (
                <Card key={r.id} className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{r.name ?? 'Untitled workflow'}</span>
                    {r.status && <Chip label={r.status} color={STATUS_COLOR[r.status] ?? null} />}
                    <Chip label={locLabel(r)} muted />
                    <span className="ml-auto shrink-0 text-xs text-slate-400" title={fmtWhen(r.createdAt)}>
                      {fmtRelative(r.createdAt)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    by <span className="font-medium text-slate-600">{r.updatedByName ?? 'Unknown'}</span>
                  </div>
                  {(r.changes?.length ?? 0) > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {r.changes!.map((ch, i) => (
                        <li key={i} className="text-sm text-slate-600">
                          <span className="mr-1.5 text-slate-300">•</span>
                          {ch}
                        </li>
                      ))}
                    </ul>
                  )}
                  {wf && (
                    <button
                      type="button"
                      onClick={() => setSelected(wf)}
                      className="mt-2 text-xs font-medium text-brand hover:underline"
                    >
                      History &amp; diff →
                    </button>
                  )}
                </Card>
              );
            })}
          </div>
        ))}

      {tab === 'workflows' &&
        (visibleWorkflows.length === 0 ? (
          <EmptyNote>No workflows tracked for this sub-account yet.</EmptyNote>
        ) : (
          <Card>
            <ul className="divide-y divide-slate-100">
              {visibleWorkflows.map((w) => (
                <li key={`${w.locationId}/${w.workflowId}`}>
                  <button
                    type="button"
                    onClick={() => setSelected(w)}
                    className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left hover:bg-slate-50"
                  >
                    <span className="text-sm font-medium text-slate-700">{w.name ?? 'Untitled workflow'}</span>
                    {w.status && <Chip label={w.status} color={STATUS_COLOR[w.status] ?? null} />}
                    <Chip label={locLabel(w)} muted />
                    <span className="ml-auto shrink-0 text-xs text-slate-400">
                      {w.stepCount} steps · {w.changeCount} change{w.changeCount === 1 ? '' : 's'}
                      {w.lastChangeAt ? ` · last ${fmtRelative(w.lastChangeAt)}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        ))}
    </div>
  );
}
